/**
 * Designed by OnHOW YouTube Channel - Anas El Medlaoui
 * Updated for Halo Garage / Shopify Horizon
 */

const onhowStyles = `
  .onhow-accordion {
    margin: 1rem 0;
    border: 1px solid #dddddd;
    border-radius: 10px;
    overflow: hidden;
    background: #ffffff;
  }

  .onhow-accordion details {
    border-bottom: 1px solid #dddddd;
  }

  .onhow-accordion details:last-child {
    border-bottom: none;
  }

  .onhow-accordion summary {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 1rem;
    padding: 1.25rem 1.5rem;
    cursor: pointer;
    background: #ffffff;
    transition: all 0.25s ease;
    list-style: none;
  }

  .onhow-accordion summary::-webkit-details-marker {
    display: none;
  }

  .onhow-accordion summary:hover {
    background: #f1f1f1;
  }

  .onhow-accordion .accordion__title {
    font-size: 1.05em;
    font-weight: 700;
    margin: 0;
    color: #222222;
    line-height: 1.4;
  }

  .onhow-accordion .accordion-icon {
    width: 34px;
    height: 34px;
    min-width: 34px;
    border-radius: 50%;
    background: #eeeeee;
    color: #000000;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.25s ease;
  }

  .onhow-accordion summary:hover .accordion-icon {
    background: #000000;
    color: #ffffff;
  }

  .onhow-accordion .accordion-icon svg {
    width: 14px;
    height: 14px;
    transition: transform 0.25s ease;
  }

  .onhow-accordion details[open] .accordion-icon {
    background: #000000;
    color: #ffffff;
  }

  .onhow-accordion details[open] .accordion-icon svg {
    transform: rotate(180deg);
  }

  .onhow-accordion .accordion__content {
    padding: 1.5rem;
    background: #ffffff;
    border-top: 1px solid #dddddd;
    animation: slowReveal 250ms ease-out;
  }

  .onhow-accordion .accordion__content p {
    margin: 0 0 1rem 0;
    line-height: 1.6;
    color: #555555;
  }

  .onhow-accordion .accordion__content p:last-child {
    margin-bottom: 0;
  }

  .onhow-accordion .accordion__content > *:first-child {
    margin-top: 0;
  }

  .onhow-accordion .accordion__content ul,
  .onhow-accordion .accordion__content ol {
    margin: 0 0 1rem 0;
    padding-left: 1.5rem;
  }

  .onhow-accordion .accordion__content li {
    margin-bottom: 0.5rem;
    line-height: 1.6;
    color: #555555;
  }

  @keyframes slowReveal {
    0% {
      opacity: 0;
      transform: translateY(-6px);
    }
    100% {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

function addStyles(stylesText) {
  const existingStyle = document.querySelector('#onhow-accordion-styles');
  if (existingStyle) return;

  const styleElement = document.createElement('style');
  styleElement.id = 'onhow-accordion-styles';
  styleElement.textContent = stylesText;
  document.head.appendChild(styleElement);
}

class OnhowDescriptionAccordion {
  constructor() {
    this.descriptionContainer = this.findContainer();

    if (!this.descriptionContainer) return;
    if (this.descriptionContainer.dataset.onhowAccordionReady === 'true') return;

    this.descriptionContainer.dataset.onhowAccordionReady = 'true';

    addStyles(onhowStyles);
    this.init();
  }

  findContainer() {
    const containers = [
      '.product__description.rte',
      'rte-formatter.rte',
      'rte-formatter'
    ];

    for (const selector of containers) {
      const elements = document.querySelectorAll(selector);

      for (const element of elements) {
        if (element.querySelectorAll('h6').length > 0) {
          return element;
        }
      }
    }

    return null;
  }

  init() {
    const accordionSections = this.findAccordionSections();

    if (accordionSections.length > 0) {
      this.createAccordion(accordionSections);
      this.hideOriginalSections();
    }
  }

  findAccordionSections() {
    const sections = [];
    const h6Elements = this.descriptionContainer.querySelectorAll('h6');

    h6Elements.forEach((h6) => {
      const title = h6.textContent.trim();
      const content = this.getContentUntilDelimiter(h6);

      if (title && content.html.trim()) {
        sections.push({
          title: title,
          content: content.html,
          originalElements: [h6, ...content.elements]
        });
      }
    });

    return sections;
  }

  getContentUntilDelimiter(h6Element) {
    const contentElements = [];
    const contentHtml = [];
    let currentElement = h6Element.nextElementSibling;

    while (currentElement) {
      const elementHtml = currentElement.outerHTML || '';
      const elementText = currentElement.textContent || '';

      if (currentElement.tagName === 'H6') {
        break;
      }

      if (elementText.includes('$')) {
        const parts = elementHtml.split('$');
        contentHtml.push(parts[0]);
        contentElements.push(currentElement);
        break;
      }

      contentHtml.push(elementHtml);
      contentElements.push(currentElement);
      currentElement = currentElement.nextElementSibling;
    }

    return {
      html: contentHtml.join(''),
      elements: contentElements
    };
  }

  createAccordion(accordionSections) {
    const accordionContainer = document.createElement('div');
    accordionContainer.className = 'onhow-accordion';

    accordionSections.forEach((section, index) => {
      const isOpen = false;

      const details = document.createElement('details');
      if (isOpen) details.setAttribute('open', '');
      details.id = `onhow-accordion-${index}`;

      const summary = document.createElement('summary');
      summary.setAttribute('role', 'button');
      summary.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
      summary.setAttribute('aria-controls', `onhow-content-${index}`);

      const title = document.createElement('h2');
      title.className = 'accordion__title';
      title.innerHTML = section.title;

      const icon = document.createElement('span');
      icon.className = 'accordion-icon';
      icon.innerHTML = `
        <svg viewBox="0 0 20 20" fill="none" aria-hidden="true" focusable="false" xmlns="http://www.w3.org/2000/svg">
          <path d="M5 7.5L10 12.5L15 7.5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      `;

      summary.appendChild(title);
      summary.appendChild(icon);

      const content = document.createElement('div');
      content.className = 'accordion__content';
      content.id = `onhow-content-${index}`;
      content.innerHTML = section.content.replace(/\$/g, '');

      details.appendChild(summary);
      details.appendChild(content);
      accordionContainer.appendChild(details);
    });

    this.descriptionContainer.insertBefore(accordionContainer, this.descriptionContainer.firstChild);
    this.initializeAccordion(accordionContainer);
  }

  hideOriginalSections() {
    const h6Elements = this.descriptionContainer.querySelectorAll('h6');

    h6Elements.forEach((h6) => {
      h6.style.display = 'none';

      let currentElement = h6.nextElementSibling;

      while (currentElement) {
        if (currentElement.tagName === 'H6') {
          break;
        }

        currentElement.style.display = 'none';

        if (currentElement.textContent.includes('$')) {
          break;
        }

        currentElement = currentElement.nextElementSibling;
      }
    });
  }

  initializeAccordion(accordionContainer) {
    const summaries = accordionContainer.querySelectorAll('summary');

    summaries.forEach((summary) => {
      summary.addEventListener('click', (event) => {
        const details = event.currentTarget.parentElement;
        const willOpen = !details.hasAttribute('open');

        event.currentTarget.setAttribute('aria-expanded', willOpen ? 'true' : 'false');
      });

      summary.addEventListener('keyup', (event) => {
        if (event.code.toUpperCase() === 'ESCAPE') {
          const details = event.currentTarget.parentElement;

          if (details.hasAttribute('open')) {
            details.removeAttribute('open');
            event.currentTarget.setAttribute('aria-expanded', 'false');
            event.currentTarget.focus();
          }
        }
      });
    });
  }
}

(function() {
  function initAccordion() {
    const containers = document.querySelectorAll('.product__description.rte, rte-formatter.rte, rte-formatter');

    if (containers.length > 0) {
      new OnhowDescriptionAccordion();
    } else if (document.readyState !== 'complete') {
      setTimeout(initAccordion, 100);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAccordion);
  } else {
    initAccordion();
  }
})();